import { useAIAssistant, type ConversationSession } from "@/contexts/AIAssistantContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MoreHorizontal, Pencil, Trash2, MessageSquarePlus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MascotConfirmDialog } from "@/components/common/MascotConfirmDialog";
import { useState } from "react";

interface ConversationHistoryProps {
  onClose?: () => void;
}

function getTimeSection(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (itemDate.getTime() === today.getTime()) return "Today";
  if (itemDate.getTime() === yesterday.getTime()) return "Yesterday";

  const diffDays = Math.floor((today.getTime() - itemDate.getTime()) / 86400000);
  if (diffDays < 7) return "This Week";
  if (diffDays < 30) return "This Month";
  return "Older";
}

function groupBySection(sessions: ConversationSession[]): [string, ConversationSession[]][] {
  const sections: Record<string, ConversationSession[]> = {};
  const order = ["Today", "Yesterday", "This Week", "This Month", "Older"];

  for (const session of sessions) {
    const section = getTimeSection(session.updatedAt);
    if (!sections[section]) sections[section] = [];
    sections[section].push(session);
  }

  return order
    .filter((key) => sections[key]?.length > 0)
    .map((key) => [key, sections[key]]);
}

export function ConversationHistory({ onClose }: ConversationHistoryProps) {
  const {
    sessions,
    currentSessionId,
    switchSession,
    deleteSession,
    renameSession,
  } = useAIAssistant();

  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const handleSessionClick = (sessionId: string) => {
    if (renamingId) return;
    if (sessionId !== currentSessionId) {
      switchSession(sessionId);
    }
    onClose?.();
  };

  const startRename = (sessionId: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(sessionId);
    setRenameValue(currentTitle);
  };

  const confirmRename = () => {
    if (renamingId && renameValue.trim()) {
      renameSession(renamingId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue("");
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const handleDelete = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionToDelete(sessionId);
  };

  const confirmDelete = () => {
    if (sessionToDelete) {
      deleteSession(sessionToDelete);
      setSessionToDelete(null);
    }
  };

  const sortedSessions = [...sessions].sort(
    (a, b) => b.updatedAt - a.updatedAt
  );

  const grouped = groupBySection(sortedSessions);

  return (
    <>
      <div className="flex flex-col max-h-[400px] overflow-hidden">
        {/* Header */}
        {sortedSessions.length > 0 && (
          <div className="flex items-center justify-between px-2.5 py-2 border-b shrink-0">
            <span className="text-xs text-muted-foreground">
              {sessions.length} conversation{sessions.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto min-h-0 scrollbar-themed">
          {sortedSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center p-8">
              <div className="w-10 h-10 rounded-xl bg-stone-100 dark:bg-stone-800/30 flex items-center justify-center mb-3">
                <MessageSquarePlus className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">
                No learning sessions yet
              </p>
              <p className="text-xs text-muted-foreground">
                Start a conversation with your learning coach
              </p>
            </div>
          ) : (
            <div className="p-1">
              {grouped.map(([section, items]) => (
                <div key={section} className="mb-1">
                  <p className="text-[11px] font-medium text-muted-foreground px-2.5 py-1.5">
                    {section}
                  </p>
                  {items.map((session) => {
                    const isActive = session.id === currentSessionId;
                    const isRenaming = renamingId === session.id;
                    return (
                      <div
                        key={session.id}
                        onClick={() => handleSessionClick(session.id)}
                        className={cn(
                          "group flex items-center px-2.5 py-1.5 rounded-md cursor-pointer transition-colors",
                          isActive
                            ? "bg-primary/10"
                            : "hover:bg-accent"
                        )}
                      >
                        {isRenaming ? (
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") confirmRename();
                              if (e.key === "Escape") cancelRename();
                            }}
                            onBlur={confirmRename}
                            autoFocus
                            className="text-[13px] flex-1 min-w-0 bg-transparent border border-primary/50 rounded px-1.5 py-0.5 outline-none"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <p
                            className={cn(
                              "text-[13px] truncate overflow-hidden whitespace-nowrap flex-1 min-w-0",
                              isActive
                                ? "text-primary font-medium"
                                : "text-foreground group-hover:text-accent-foreground"
                            )}
                          >
                            {session.title}
                          </p>
                        )}
                        {!isRenaming && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={(e) => startRename(session.id, session.title, e)}
                              >
                                <Pencil className="h-3.5 w-3.5 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleDelete(session.id, e)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MascotConfirmDialog
        open={!!sessionToDelete}
        onOpenChange={(open) => {
          if (!open) setSessionToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Conversation"
        description="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
      />
    </>
  );
}
